import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';
import {
    DeliveryCreatePayload,
    DeliveryFetchFilters,
    DeliveryMeta,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';

/**
 * DeliveryAPI - API controller for logistics deliveries/shipments
 * Handles CRUD operations for delivery lists
 */
export default class DeliveryAPI {
    /**
     * Get all deliveries
     * GET /logistics/shipments
     */
    public static async getAll(filters?: DeliveryFetchFilters): Promise<AxiosResponse> {
        const queryParts = [
            `page[number]=${filters?.pageNumber ?? 1}`,
            `page[size]=${filters?.pageSize ?? 10}`,
            'sort=-id',
        ];

        if (filters?.date) {
            queryParts.push(`filter[date]=${encodeURIComponent(filters.date)}`);
        }

        try {
            const response = await ApiController.get(`${ApiRoutes.deliveries.getAll}&${queryParts.join('&')}`);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Get a single delivery by ID
     * GET /logistics/shipments/{id}
     */
    public static async getById(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.deliveries.getById(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Get delivery PDF
     * GET /logistics/shipments/{id}/pdf
     */
    public static async getPdf(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.deliveries.getPdf(id), null, true);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new delivery
     * POST /logistics/shipments
     * Sends selected internal order IDs
     */
    public static async create(payload: DeliveryCreatePayload): Promise<AxiosResponse> {
        const request = {
            meta: payload.meta,
            data: payload.data,
        };

        try {
            const response = await ApiController.post(ApiRoutes.deliveries.create, request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Add an internal order to an existing delivery
     * POST /logistics/shipments/{id}/items/project-internal-order
     */
    public static async addNewInternalOrderToList(
        id: string,
        projectInternalOrderId: number,
    ): Promise<AxiosResponse> {
        try {
            const response = await ApiController.post(ApiRoutes.deliveries.addNewInternalOrderToList(id), {
                projectInternalOrderId,
            });
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing delivery
     * PATCH /logistics/shipments/{id}
     */
    public static async update(id: string, payload: Partial<DeliveryMeta>): Promise<AxiosResponse> {
        const request = {
            data: {
                attributes: {
                    ...payload,
                },
                type: 'delivery_lists',
                id,
            },
        };

        try {
            const response = await ApiController.patch(ApiRoutes.deliveries.update(id), request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a delivery
     * DELETE /logistics/shipments/{id}
     */
    public static async delete(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.delete(ApiRoutes.deliveries.delete(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

}
